import { useContext, useState } from "react";

import { useTranslation } from "react-i18next";

import {
    DeleteSweep1Icon,
    DownloadDoneIcon
} from "@dotnet/telematics-design-system";

import InlineActionIconButton from "components/DesignComponents/Button/InlineActionIconButton";
import { ApproveDiscardProps } from "components/TaskGroups/types/interfaces";
import AlertContext, { ALERT_STATUS } from "context/alert/alertContext";
import ParamsContext from "context/params/paramsContext";
import { useApi } from "shared/hooks/useApi";
import { ENDPOINTS, TABLE_NAMES } from "shared/types/enums";

import {
    APPROVE,
    DISCARD,
    FORBIDDEN_TO_DELETE_BATCH,
    FORBIDDEN_TO_UPDATE_BATCH
} from "./constants";

const ApproveDiscard = ({ id }: ApproveDiscardProps) => {
    const { setAlert } = useContext(AlertContext);
    const { setReloadItems } = useContext(ParamsContext);

    const { t } = useTranslation();

    const { postData, handleResponse, isCanceled, getCriticalMessage } =
        useApi();

    const [isLoading, setIsLoading] = useState({
        approve: false,
        discard: false
    });

    const handleError = (error: any, isApproveAction: boolean) => {
        const forbiddenMessage = isApproveAction
            ? FORBIDDEN_TO_UPDATE_BATCH
            : FORBIDDEN_TO_DELETE_BATCH;

        const { message } = getCriticalMessage(error);
        const isForbidden = message === forbiddenMessage;

        if (isForbidden) {
            const actionName = isApproveAction ? APPROVE : DISCARD;

            setAlert({
                status: ALERT_STATUS.Critical,
                title: t(
                    `Flash##not allowed to affect batch##title##${actionName}`
                ),
                description: t(
                    "Flash##not allowed to affect batch##description"
                )
            });
        } else {
            handleResponse(error);
        }
    };

    const handleApprove = async () => {
        try {
            setIsLoading({ ...isLoading, approve: true });

            const response = await postData(
                `${ENDPOINTS.Batches}/${id}/${APPROVE}`
            );

            handleResponse(response);
            setReloadItems(TABLE_NAMES.TaskGroups);
        } catch (error) {
            if (!isCanceled(error)) {
                handleError(error, true);
            }
        }

        setIsLoading({ ...isLoading, approve: false });
    };

    const handleDiscard = async () => {
        try {
            setIsLoading({ ...isLoading, discard: true });

            const response = await postData(
                `${ENDPOINTS.Batches}/${id}/${DISCARD}`
            );

            handleResponse(response);
            setReloadItems(TABLE_NAMES.TaskGroups);
        } catch (error) {
            if (!isCanceled(error)) {
                handleError(error, false);
            }
        }

        setIsLoading({ ...isLoading, discard: false });
    };

    return (
        <>
            <InlineActionIconButton
                title={t(`Button##${DISCARD}`)}
                icon={<DeleteSweep1Icon />}
                onButtonClick={handleDiscard}
                isLoading={isLoading.discard}
            />

            <InlineActionIconButton
                title={t(`Button##${APPROVE}`)}
                icon={<DownloadDoneIcon />}
                onButtonClick={handleApprove}
                isLoading={isLoading.approve}
            />
        </>
    );
};

export default ApproveDiscard;
